import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { IStoreSettings } from '../../../../../../../common/common-interfaces/store-settings';
import { Api } from '../../../services/api';
import { Globals } from '../../../services/globals';
import moment from 'moment';
import { Utility } from 'src/app/services/utility';
import { ChartDataset, ChartOptions, LegendOptions, ChartType } from 'chart.js';
import { IPosterOrder } from '../../../../../../../common/common-interfaces/poster-order';
import { NgZone } from '@angular/core';

@Component({
    selector: 'dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false
})
export class DashboardComponent {
    stores: IStoreSettings[];
    storesById: { [id: string]: IStoreSettings } = {};

    aceStores: IStoreSettings[];
    partnerStores: IStoreSettings[];

    periodStart: Date;
    periodEnd: Date;

    salesSummary: any;

    dayGraphData: {
        datasets: ChartDataset[],
        labels: string[],
        options: ChartOptions,
        legend?: LegendOptions<any>,
        chartType: ChartType
    };

    weekGraphData: {
        datasets: ChartDataset[],
        labels: string[],
        options: ChartOptions,
        legend?: LegendOptions<any>,
        chartType: ChartType
    };

    monthGraphData: {
        datasets: ChartDataset[],
        labels: string[],
        options: ChartOptions,
        legend?: LegendOptions<any>,
        chartType: ChartType
    };

    latestOrders: IPosterOrder[] = [];

    newOrderSound = new Audio('/gateway-app/browser/assets/cashing.mp3');

    storeColors = {
        'minposter': '#3b57ff',
        'barnposters': '#57ebff',
        'tapetgiganten': '#ffc26a',
        'bildbyrån': '#ff1db1',
        'fyndiq': '#ff8295',
        'partners': '#ffffff'
    };

    partnerColors = [
        '#ffaa00',
        '#fff800',
        '#00d7ff',
        '#f000ff',
        '#e6ff00',
        '#00ffb2',
        '#0079ff',
        '#f485ff',
        '#ffe585'
    ];

    totalsByMonth: { month: string, sales: number, costs: number, result: number }[] = [];
    totalsByYear: { [year: string]: { year: string, sales: number, costs: number, result: number } };


    hideMenu = false;

    constructor(private api: Api, private router: Router, private route: ActivatedRoute, public globals: Globals, public utility: Utility, private zone: NgZone) {

    }

    async ngOnInit() {

        if (window.location.hash.includes('hideMenu')) {
            (document.querySelector('.menu') as any).style.display = 'none';
            this.hideMenu = true;
        }


        this.periodStart = moment().subtract(24, 'months').toDate();
        this.periodEnd = moment().toDate();
        await this.fetchStores();

        if (this.globals.currentUser) {
            await this.fetchSalesSummary();
        }

        await this.fetchSalesByDay();
        await this.fetchSalesByWeek();
        await this.fetchSalesByMonth();
        await this.fetchLatestOrders();


        setInterval(() => {
            this.zone.run(() => {
                this.fetchLatestOrders();
            });
        }, 1000 * 60 * 4);


        setInterval(() => {
            this.zone.run(() => {
                this.fetchSalesByWeek();
            });
        }, 1000 * 60 * 10);
    }

    abs(val) {
        return Math.abs(val);
    }



    async fetchStores() {
        this.stores = await this.api.admin().get<any>(`/stores`);
        this.aceStores = [];
        this.partnerStores = [];

        if (this.stores && this.stores.length) {
            for (let store of this.stores) {

                this.storesById[store._id] = store;

                if (store.type == 'partner')
                    this.partnerStores.push(store);
                else
                    this.aceStores.push(store);

            }
        }
    }


    async fetchSalesSummary() {
        this.salesSummary = await this.api.admin().get<any>(`/statistics/sales-summary`);
    }

    async fetchSalesByDay() {
        let periodStart = moment().subtract(10, 'weeks').toDate();
        let periodEnd = moment().toDate();
        let data = await this.api.admin().get<any>(`/statistics/sales-by-day?startDate=${moment(periodStart).toISOString()}&endDate=${moment(periodEnd).toISOString()}&storeIds=${this.aceStores.concat(this.partnerStores).map(s => s._id).join(',')}`);
        this.dayGraphData = {
            chartType: 'bar',
            datasets: [],
            labels: [],
            options: {
                plugins: {
                    legend: {
                        display: true,
                        labels: {
                            color: '#76c5e0'
                        }
                    },
                    tooltip: {
                        mode: 'index',
                        callbacks: {
                            label: (item) => {
                                var label = item.dataset.label || '';
                                if (label) {
                                    label += ': ';
                                }
                                label += item.parsed.y;
                                return label;
                            }
                        }
                    }
                },

                elements: {
                    line: {
                        tension: .3
                    }
                },
                responsive: true,

                hover: {
                    mode: 'index'
                },
                scales: {
                    x: {
                        stacked: true,
                        ticks: {
                            autoSkip: true,
                            maxTicksLimit: 20,
                            color: '#76c5e0'
                        },
                        grid: {
                            color: `#76c5e05e`
                        }
                    },
                    y: {
                        stacked: true,
                        ticks: {
                            color: '#76c5e0'
                        },
                        grid: {
                            color: `#76c5e05e`
                        }
                    }
                }
            }
        };

        this.dayGraphData.labels = data.days.map(w => w.split('-').slice(1).join('-'));


        let partnerData = [];
        let partnerCount = 0;
        for (let storeId in data.storeData) {

            if (this.storesById[storeId].type == 'ace') {
                this.dayGraphData.datasets.push({
                    label: this.storesById[storeId].name,
                    borderColor: `${this.storeColorById(storeId)}6b`,
                    borderWidth: 2,
                    backgroundColor: `${this.storeColorById(storeId)}6b`,
                    pointBackgroundColor: `${this.storeColorById(storeId)}6b`,
                    pointBorderColor: `${this.storeColorById(storeId)}6b`,
                    fill: true,
                    stack: '0',
                    data: Object.values(data.storeData[storeId].dayData).map((data: any) => {
                        return Math.round(data.sales);
                    })
                });
            }
            else if (!this.storesById[storeId].name.includes('test')) {
                this.dayGraphData.datasets.push({
                    label: this.storesById[storeId].name,
                    borderColor: this.partnerColors[partnerCount],
                    borderWidth: 2,
                    backgroundColor: `${this.partnerStoreColor()}d1`,
                    pointBackgroundColor: `${this.partnerStoreColor()}d1`,
                    pointBorderColor: `${this.partnerStoreColor()}d1`,
                    fill: true,
                    stack: '0',
                    data: Object.values(data.storeData[storeId].dayData).map((data: any) => {
                        return Math.round(data.sales);
                    })
                });
                partnerCount++;

            }


        }


        this.dayGraphData.datasets.push({
            label: 'Kostnader',
            borderColor: '#de58846b',
            borderWidth: 0,
            backgroundColor: '#de58846b',
            pointBackgroundColor: '#de58846b',
            pointBorderColor: '#de58846b',
            fill: true,
            stack: '1',
            data: Object.values(data.costs).map((data: number) => {
                return Math.round(data);
            })
        });

        this.dayGraphData.datasets.push({
            label: 'Resultat',
            borderColor: '#4cff00',
            borderWidth: 0,
            backgroundColor: '#4cff00',
            pointBackgroundColor: '#4cff00',
            pointBorderColor: '#4cff00',
            fill: true,
            stack: '2',
            data: Object.values(data.results).map((data: number) => {
                return Math.round(data);
            })
        });
    }

    async fetchSalesByWeek() {
        let data = await this.api.admin().get<any>(`/statistics/sales-by-week?startDate=${moment(this.periodStart).toISOString()}&endDate=${moment(this.periodEnd).toISOString()}&storeIds=${this.aceStores.concat(this.partnerStores).map(s => s._id).join(',')}`);
        this.weekGraphData = {
            chartType: 'bar',
            datasets: [],
            labels: [],
            options: {
                plugins: {
                    legend: {
                        display: true,
                        labels: {
                            color: '#76c5e0'
                        }
                    },
                    tooltip: {
                        mode: 'index',
                        callbacks: {
                            label: (item) => {
                                var label = item.dataset.label || '';
                                if (label) {
                                    label += ': ';
                                }
                                label += item.parsed.y;
                                return label;
                            }
                        }
                    }
                },

                elements: {
                    line: {
                        tension: .3
                    }
                },
                responsive: true,

                hover: {
                    mode: 'index'
                },
                scales: {
                    x: {
                        stacked: true,
                        ticks: {
                            autoSkip: true,
                            maxTicksLimit: 20,
                            color: '#76c5e0'
                        },
                        grid: {
                            color: `#76c5e05e`
                        }
                    },
                    y: {
                        stacked: true,
                        ticks: {
                            color: '#76c5e0'
                        },
                        grid: {
                            color: `#76c5e05e`
                        }
                    }
                }
            }
        };

        this.weekGraphData.labels = data.weeks.map(w => w.split('-')[0]);


        let partnerData = [];
        let partnerCount = 0;
        for (let storeId in data.storeData) {

            if (this.storesById[storeId].type == 'ace') {
                this.weekGraphData.datasets.push({
                    label: this.storesById[storeId].name,
                    borderColor: `${this.storeColorById(storeId)}6b`,
                    borderWidth: 2,
                    backgroundColor: `${this.storeColorById(storeId)}6b`,
                    pointBackgroundColor: `${this.storeColorById(storeId)}6b`,
                    pointBorderColor: `${this.storeColorById(storeId)}6b`,
                    fill: true,
                    stack: '0',
                    data: Object.values(data.storeData[storeId].weekData).map((data: any) => {
                        return Math.round(data.sales);
                    })
                });
            }
            else if (!this.storesById[storeId].name.includes('test')) {
                this.weekGraphData.datasets.push({
                    label: this.storesById[storeId].name,
                    borderColor: this.partnerColors[partnerCount],
                    borderWidth: 2,
                    backgroundColor: `${this.partnerStoreColor()}d1`,
                    pointBackgroundColor: `${this.partnerStoreColor()}d1`,
                    pointBorderColor: `${this.partnerStoreColor()}d1`,
                    fill: true,
                    stack: '0',
                    data: Object.values(data.storeData[storeId].weekData).map((data: any) => {
                        return Math.round(data.sales);
                    })
                });
                partnerCount++;
                /*
                if (!partnerData.length) {
                  partnerData = Object.values(data.storeData[storeId].weekData).map((data: any) => {
                    return Math.round(data.sales);
                  });
                }
                else {
                  Object.values(data.storeData[storeId].weekData).forEach((data: any, i) => {
                    partnerData[i] += Math.round(data.sales);
                  });
                }
                */
            }


        }

        /*
            this.weekGraphData.datasets.push({
              label: 'partners',
              borderColor: `${this.partnerStoreColor()}d1`,
              borderWidth: 1,
              backgroundColor: `${this.partnerStoreColor()}d1`,
              pointBackgroundColor: `${this.partnerStoreColor()}d1`,
              pointBorderColor: `${this.partnerStoreColor()}d1`,
              fill: true,
              stack: '0',
              data: partnerData
            });
        */

        this.weekGraphData.datasets.push({
            label: 'Kostnader',
            borderColor: '#de58846b',
            borderWidth: 0,
            backgroundColor: '#de58846b',
            pointBackgroundColor: '#de58846b',
            pointBorderColor: '#de58846b',
            fill: true,
            stack: '1',
            data: Object.values(data.costs).map((data: number) => {
                return Math.round(data);
            })
        });

        this.weekGraphData.datasets.push({
            label: 'Resultat',
            borderColor: '#4cff00',
            borderWidth: 0,
            backgroundColor: '#4cff00',
            pointBackgroundColor: '#4cff00',
            pointBorderColor: '#4cff00',
            fill: true,
            stack: '2',
            data: Object.values(data.results).map((data: number) => {
                return Math.round(data);
            })
        });
    }



    async fetchSalesByMonth() {
        let data = await this.api.admin().get<any>(`/statistics/sales-by-month?startDate=${moment(this.periodStart).toISOString()}&endDate=${moment(this.periodEnd).toISOString()}&storeIds=${this.aceStores.concat(this.partnerStores).map(s => s._id).join(',')}`);
        this.monthGraphData = {
            chartType: 'bar',
            datasets: [],
            labels: [],
            options: {
                plugins: {
                    legend: {
                        display: true,
                        labels: {
                            color: '#76c5e0'
                        }
                    },
                    tooltip: {
                        mode: 'index',
                        callbacks: {
                            label: (item) => {
                                var label = item.dataset.label || '';
                                if (label) {
                                    label += ': ';
                                }
                                label += item.parsed.y;
                                return label;
                            }
                        }
                    }
                },

                elements: {
                    line: {
                        tension: .3
                    }
                },
                responsive: true,

                hover: {
                    mode: 'index'
                },
                scales: {
                    x: {
                        stacked: true,
                        ticks: {
                            autoSkip: true,
                            maxTicksLimit: 20,
                            color: '#76c5e0'
                        },
                        grid: {
                            color: `#76c5e05e`
                        }
                    },
                    y: {
                        stacked: true,
                        ticks: {
                            color: '#76c5e0'
                        },
                        grid: {
                            color: `#76c5e05e`
                        }
                    }
                }
            }
        };

        this.monthGraphData.labels = data.months.map(w => w.split('-')[0]);


        let partnerData = [];
        let partnerCount = 0;
        for (let storeId in data.storeData) {

            if (this.storesById[storeId].type == 'ace') {
                this.monthGraphData.datasets.push({
                    label: this.storesById[storeId].name,
                    borderColor: `${this.storeColorById(storeId)}6b`,
                    borderWidth: 2,
                    backgroundColor: `${this.storeColorById(storeId)}6b`,
                    pointBackgroundColor: `${this.storeColorById(storeId)}6b`,
                    pointBorderColor: `${this.storeColorById(storeId)}6b`,
                    fill: true,
                    stack: '0',
                    data: Object.values(data.storeData[storeId].monthData).map((data: any) => {
                        return Math.round(data.sales);
                    })
                });
            }
            else if (!this.storesById[storeId].name.includes('test')) {
                this.monthGraphData.datasets.push({
                    label: this.storesById[storeId].name,
                    borderColor: this.partnerColors[partnerCount],
                    borderWidth: 2,
                    backgroundColor: `${this.partnerStoreColor()}d1`,
                    pointBackgroundColor: `${this.partnerStoreColor()}d1`,
                    pointBorderColor: `${this.partnerStoreColor()}d1`,
                    fill: true,
                    stack: '0',
                    data: Object.values(data.storeData[storeId].monthData).map((data: any) => {
                        return Math.round(data.sales);
                    })
                });
                partnerCount++;
                /*
                if (!partnerData.length) {
                  partnerData = Object.values(data.storeData[storeId].monthData).map((data: any) => {
                    return Math.round(data.sales);
                  });
                }
                else {
                  Object.values(data.storeData[storeId].monthData).forEach((data: any, i) => {
                    partnerData[i] += Math.round(data.sales);
                  });
                }
                */
            }


        }

        /*
            this.monthGraphData.datasets.push({
              label: 'partners',
              borderColor: `${this.partnerStoreColor()}d1`,
              borderWidth: 0,
              backgroundColor: `${this.partnerStoreColor()}d1`,
              pointBackgroundColor: `${this.partnerStoreColor()}d1`,
              pointBorderColor: `${this.partnerStoreColor()}d1`,
              fill: true,
              stack: '0',
              data: partnerData
            });
            */


        this.monthGraphData.datasets.push({
            label: 'Kostnader',
            borderColor: '#de58846b',
            borderWidth: 0,
            backgroundColor: '#de58846b',
            pointBackgroundColor: '#de58846b',
            pointBorderColor: '#de58846b',
            fill: true,
            stack: '1',
            data: Object.values(data.costs).map((data: number) => {
                return Math.round(data);
            })
        });

        this.monthGraphData.datasets.push({
            label: 'Resultat',
            borderColor: '#4cff00',
            borderWidth: 0,
            backgroundColor: '#4cff00',
            pointBackgroundColor: '#4cff00',
            pointBorderColor: '#4cff00',
            fill: true,
            stack: '2',
            data: Object.values(data.results).map((data: number) => {
                return Math.round(data);
            })
        });

        this.totalsByMonth = [];
        this.totalsByYear = {};
        for (let i = 0; i < data.months.length; i++) {
            this.totalsByMonth.push({
                month: data.months[i],
                sales: Object.values(data.storeData).reduce((sum, val: any) => sum + val.monthData[i].sales, 0) as number,
                costs: data.costs[i],
                result: data.results[i]
            });

            let year = data.months[i].split('-')[1];
            if (!this.totalsByYear[year]) {
                this.totalsByYear[year] = {
                    year: year,
                    sales: 0,
                    costs: 0,
                    result: 0
                };
            }

            this.totalsByYear[year].sales += this.totalsByMonth[i].sales;
            this.totalsByYear[year].costs += this.totalsByMonth[i].costs;
            this.totalsByYear[year].result += this.totalsByMonth[i].result;
        }
    }


    storeColorById(storeId: string) {
        return this.storeColors[this.storesById[storeId].name] ? this.storeColors[this.storesById[storeId].name] : '#ffffff';
    }

    partnerStoreColor() {
        return this.storeColors['partners'];
    }

    async newOrder() {
        this.newOrderSound.play();
    }

    async fetchLatestOrders() {
        let previous = this.latestOrders;
        this.latestOrders = await this.api.admin().get<any>(`/statistics/latest-orders`);

        if (previous.length && previous[0]._id != this.latestOrders[0]._id) {
            this.newOrder();
        }

    }

}
